import { CreateDonationRequestModeEnum, Project, ProjectSettingsPaymentGatewayEnum } from '@/app/api';
import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';
import { resolveIsApplePaymentMethodSupported } from '@/app/components/PaymentMethods/PaymentMethods';
import { MixpanelEventSource, PaymentMethod } from '@/app/enums';
import { pushAdditionalGaIds } from '@/app/hooks/useAnalytics/push-additional-ga-ids';
import { pushToDataLayer } from '@/app/hooks/useAnalytics/push-to-data-layer';
import {
  CheckoutStepNumbers,
  CheckoutSteps,
  CheckoutTypes,
  getProjectBrand,
  getProjectCategory,
  GoogleTagManagerEventNames,
  PageTypes,
} from '@/app/hooks/useAnalytics/types';
import { useCurrentUser } from '@/app/hooks/useCurrentUser';
import { PaymentMethodFormType, usePaymentMethods } from '@/app/hooks/usePaymentMethods';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { envs } from '@/app/utils/envs';
import mixpanel from 'mixpanel-browser';
import { useContext, useEffect } from 'react';

export const publishGtmDonationFormPageViewEvent = (project: Project, abGroup: string) => {
  const gtmObject = {
    ecommerce: {
      items: [
        {
          item_id: project.numericId,
          item_name: project.content.baseName,
          item_brand: getProjectBrand(project),
          item_category: getProjectCategory(project),
          abGroup,
        },
      ],
    },
  };

  pushAdditionalGaIds(GoogleTagManagerEventNames.BeginCheckout, project);
  pushToDataLayer(gtmObject);
};

export const useCheckoutPageViewAnalytics = (formType: PaymentMethodFormType, mode?: CreateDonationRequestModeEnum) => {
  const { areStatisticsCookiesAllowed } = useContext(CookiebotContext);
  const { currentUser, isLoading: isLoadingUser } = useCurrentUser();

  const abGroup = useAbGroup();
  const { project } = useProjectData();
  const paymentMethods = usePaymentMethods(formType, mode);

  useEffect(() => {
    const isApplePaySupported =
      resolveIsApplePaymentMethodSupported() &&
      project.settings.paymentGateway === ProjectSettingsPaymentGatewayEnum.Trustpay;

    const filteredPaymentMethods = paymentMethods.filter(
      (item) => item !== PaymentMethod.ApplePay || isApplePaySupported,
    );

    if (areStatisticsCookiesAllowed && !isLoadingUser) {
      const pageviewObject = {
        page_type: PageTypes.Checkout,
        checkout_type: project.settings.isGoodHabit ? CheckoutTypes.Subscription : CheckoutTypes.OneTime,
        step_name: CheckoutSteps.DonationForm,
        step_number: CheckoutStepNumbers.DonationForm,
        project_id: project.numericId,
        project_name: project.content.baseName,
        project_mode: project.settings.modeGroup,
        project_main_category: project.settings.taxonomy.mainCategory?.name,
        project_categories: project.settings.taxonomy.categories?.map((category) => category.name),
        topic: project.settings.taxonomy.topic?.name,
        abGroup,
        paymentMethods: filteredPaymentMethods,
        defaultDonationMode: mode,
        formType,
        // TODO mixpanel.identify workaround
        $user_id: currentUser?.id,
        platformSource: MixpanelEventSource.NextJs,
      };

      mixpanel.track_pageview(pageviewObject);
    }
  }, [areStatisticsCookiesAllowed, isLoadingUser]);

  useEffect(() => {
    if (areStatisticsCookiesAllowed && envs.isGtmEnabled) {
      publishGtmDonationFormPageViewEvent(project, abGroup);
    }
  }, [areStatisticsCookiesAllowed]);
};
